import { memo } from 'react';
import { Send, Mail, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { NotificationChannel } from '@masters-club/types';

interface ChannelOption {
  id: NotificationChannel;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}

const CHANNELS: ChannelOption[] = [
  {
    id: 'TELEGRAM',
    label: 'Telegram',
    description: 'Сообщение в Telegram-бот: текст, медиа, кнопки',
    icon: Send,
  },
  {
    id: 'EMAIL',
    label: 'Эл. почта',
    description: 'Письмо на email: визуальный редактор или текст',
    icon: Mail,
  },
];

interface ChannelSelectorProps {
  channels: NotificationChannel[];
  toggleChannel: (checked: boolean, channel: NotificationChannel) => void;
  disabled?: boolean;
}

export const ChannelSelector = memo(function ChannelSelector({
  channels,
  toggleChannel,
  disabled,
}: ChannelSelectorProps) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {CHANNELS.map((channel) => {
        const selected = channels.includes(channel.id);
        const Icon = channel.icon;
        return (
          <button
            key={channel.id}
            type="button"
            disabled={disabled}
            onClick={() => toggleChannel(!selected, channel.id)}
            aria-pressed={selected}
            className={cn(
              'group relative flex flex-col items-start gap-2 rounded-lg border p-4 text-left transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'disabled:cursor-not-allowed disabled:opacity-60',
              selected
                ? 'border-primary bg-primary/5 ring-1 ring-primary'
                : 'hover:border-muted-foreground/40 hover:bg-muted/40',
            )}
          >
            <span
              className={cn(
                'absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full border transition-colors',
                selected
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-muted-foreground/30 text-transparent',
              )}
            >
              <Check className="h-3 w-3" />
            </span>
            <span
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-md',
                selected ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
              )}
            >
              <Icon className="h-5 w-5" />
            </span>
            <span className="font-medium text-sm">{channel.label}</span>
            <span className="text-xs text-muted-foreground leading-snug">
              {channel.description}
            </span>
          </button>
        );
      })}
    </div>
  );
});
